import {
  referenceSoundsIntoOpenMic,
  type MelodyTrainerMode,
  type PracticeLevel,
  type VocalLib,
} from '@tunasong/music-lib'
import type { Melody } from '@tunasong/schemas'
import { Button, Checkbox, FormControlLabel, Radio, RadioGroup } from '@tunasong/ui-lib'
import { useState } from 'react'
import type { FC } from 'react'
import { MelodyMiniRoll } from '../melody-roll/melody-mini-roll.js'
import { useVocalCopy } from '../pitch-training/use-vocal-copy.js'
import { TheoryGlyphs } from '../theory-copy/theory-glyphs.js'
import styles from './melody-trainer-welcome.module.css'

/**
 * The Melody Trainer welcome screen: pick how you are going to play it, clear the prerequisites,
 * start.
 *
 * ONE prerequisite is a GATE rather than a warning, and it is the whole reason this screen
 * exists: without headphones the click is captured on every beat and segmented as notes (§5).
 * That is not something the tool can work around, so `Start` stays disabled and the locked
 * sentence says which one is missing.
 *
 * The vocal range is NOT a second gate, and this is the one place the Melody Trainer parts
 * company with the Pitch Trainer. The Pitch Trainer GENERATES exercises, so it cannot place a
 * single one until it knows the voice (vocal §5.8). A melody already exists at a pitch - and if it
 * was captured by singing it is provably singable - so here the range is an ACCOMMODATION: with
 * one, the melody moves to fit the voice; without one, it is sung where it is written, and the
 * screen says so rather than locking the tool (Bowie, 2026-09-11). Measuring is offered either
 * way, because re-measuring a voice is legitimate.
 *
 * There used to be a second gate here - an eight-click input-latency tap test (§6.3) - and it is
 * gone. It timed taps against count-in clicks played through `engine.play` while the reference the
 * singer performs against goes out through `playScheduled`: a ~120 ms discrepancy calibrated on
 * one path and applied to the other, against a 25 ms "on" band. The timing layer now reports
 * placement relative to the phrase's own mean, which needs no calibration, so the ritual bought
 * nothing and cost every session eight clicks (Bowie, 2026-09-13).
 *
 * The one sentence here that is NOT a lexicon verdict is the transposition notice. §6 requires the
 * transposition be stated, and it describes what the *tool* did rather than what the *user* did -
 * so it is UI copy, like the mode labels beside it.
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §6, §10
 */

const MODE_LABEL: Record<MelodyTrainerMode, string> = {
  voice: 'Voice — sing it',
  instrument: 'Instrument — guitar or piano, through the microphone',
  midi: 'MIDI — play it on a connected keyboard',
}

/** Which modes open a microphone, and therefore need headphones. */
const isAudioMode = (mode: MelodyTrainerMode) => mode !== 'midi'

/** Whole octaves only, so this never has to name an interval. */
const transpositionNotice = (semitones: number): string | null => {
  if (semitones === 0) {
    return null
  }
  const octaves = Math.abs(semitones) / 12
  const direction = semitones < 0 ? 'down' : 'up'
  const count = octaves === 1 ? 'an octave' : `${octaves} octaves`
  return `Transposed ${direction} ${count} to fit your range.`
}

export interface MelodyTrainerWelcomeProps {
  melody: Melody
  /** Pre-selected mode from `?mode=`, or null for no pre-selection. */
  preSelected: MelodyTrainerMode | null
  /** The stored vocal range, or null when the singer has not calibrated. */
  range: VocalLib.VocalRange | null
  deviceLabel: string | null
  headphonesConfirmed: boolean
  midiAvailable: boolean
  /** Semitones voice mode would move the melody by. 0 when it fits as it stands. */
  transposedBy: number
  /**
   * §12's two rungs. Defaults to Level 1 - the level a melody not yet practised starts at, because
   * a singer matches a sounding pitch long before they can recall one.
   */
  level?: PracticeLevel
  /**
   * Absent for a host that does not run levels, and then no picker is rendered: a radio group
   * wired to nothing is worse than no radio group.
   */
  onLevelChange?: (level: PracticeLevel) => void
  onConfirmHeadphones: (confirmed: boolean) => void
  /** Run the two-take range measurement - the Pitch Trainer's, not a second one. */
  onCalibrateRange: () => void
  onStart: (mode: MelodyTrainerMode) => void
}

export const MelodyTrainerWelcome: FC<MelodyTrainerWelcomeProps> = props => {
  const {
    melody,
    preSelected,
    range,
    deviceLabel,
    headphonesConfirmed,
    midiAvailable,
    transposedBy,
    level = 'sing-along',
    onLevelChange,
    onConfirmHeadphones,
    onCalibrateRange,
    onStart,
  } = props

  const [mode, setMode] = useState<MelodyTrainerMode>(preSelected ?? 'voice')

  const intro = useVocalCopy('timing-intro')
  const unmeasured = useVocalCopy('vocal-range-unmeasured')
  const singAlong = useVocalCopy('vocal-level-sing-along')
  const singBack = useVocalCopy('vocal-level-sing-back')
  /*
   * WHICH headphone sentence is decided by the CAUSE, never by the level's name: at Level 1 the
   * reference sounds into an open microphone (§12.2), which is the same condition §5's gate has
   * always described. Same predicate, so a future mode that sounds something during a take
   * inherits the requirement without this file being edited.
   */
  const headphonesRequired = referenceSoundsIntoOpenMic({ level, mode })
  const headphones = useVocalCopy(headphonesRequired ? 'vocal-headphones-required' : 'vocal-headphones')
  const privacy = useVocalCopy('vocal-privacy')

  const audio = isAudioMode(mode)
  const voice = mode === 'voice'
  const rangeUnmeasured = voice && range === null
  const needsHeadphones = audio && !headphonesConfirmed
  const notice = voice && range ? transpositionNotice(transposedBy) : null

  const canStart = melody.notes.length > 0 && !needsHeadphones

  return (
    <section className={styles.welcome} aria-label="Melody Trainer welcome">
      <h2 className={styles.title}>Practice this melody</h2>

      {intro ? (
        <p className={styles.intro}>
          <TheoryGlyphs>{intro.body}</TheoryGlyphs>
        </p>
      ) : null}

      <MelodyMiniRoll melody={melody} className={styles.roll} />

      <RadioGroup
        aria-label="How you will play it"
        name="melody-trainer-mode"
        value={mode}
        onChange={(_event, value) => setMode(value as MelodyTrainerMode)}
        className={styles.modes}
      >
        <FormControlLabel value="voice" control={<Radio />} label={MODE_LABEL.voice} />
        <FormControlLabel value="instrument" control={<Radio />} label={MODE_LABEL.instrument} />
        <FormControlLabel
          value="midi"
          /* No input, no mode: a MIDI attempt with nothing connected would record silence. */
          control={<Radio disabled={!midiAvailable} />}
          label={MODE_LABEL.midi}
        />
      </RadioGroup>

      {onLevelChange && singAlong && singBack ? (
        <RadioGroup
          aria-label="How much help you want"
          name="melody-trainer-level"
          value={level}
          onChange={(_event, value) => onLevelChange(value as PracticeLevel)}
          className={styles.levels}
        >
          <FormControlLabel value="sing-along" control={<Radio />} label={singAlong.body} />
          <FormControlLabel value="sing-back" control={<Radio />} label={singBack.body} />
        </RadioGroup>
      ) : null}

      {notice ? <p className={styles.notice}>{notice}</p> : null}

      {rangeUnmeasured && unmeasured ? (
        <p className={styles.ready}>
          <TheoryGlyphs>{unmeasured.body}</TheoryGlyphs>
        </p>
      ) : null}

      {/* Offered whenever the voice is the instrument: re-measuring a voice is legitimate, and
          a voice that has never been measured is the only case that needs pressing about it. */}
      {voice ? (
        <Button variant={rangeUnmeasured ? 'outlined' : 'text'} onClick={onCalibrateRange}>
          Find my range
        </Button>
      ) : null}

      {melody.notes.length === 0 ? (
        <p className={styles.problem}>This melody has no notes yet — add some before practising it.</p>
      ) : null}

      {audio ? (
        <div className={styles.headphones}>
          <FormControlLabel
            label="I am wearing headphones"
            control={<Checkbox checked={headphonesConfirmed} onCheckedChange={onConfirmHeadphones} />}
          />
          {headphones ? (
            <span className={styles.headphonesWhy}>
              <TheoryGlyphs>{headphones.body}</TheoryGlyphs>
            </span>
          ) : null}
        </div>
      ) : null}

      {audio && privacy ? (
        <p className={styles.privacy}>
          <TheoryGlyphs>{privacy.body}</TheoryGlyphs>
        </p>
      ) : null}

      <div className={styles.actions}>
        <Button variant="contained" color="primary" size="large" disabled={!canStart} onClick={() => onStart(mode)}>
          Start
        </Button>
        {deviceLabel ? <span className={styles.device}>{deviceLabel}</span> : null}
      </div>
    </section>
  )
}
